import React, { useState, useMemo, useRef, useEffect } from 'react';
import vsaasTheme from '../theme';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Chip,
  Collapse,
  Tooltip,
  Popover,
} from '@mui/material';
import DynamicFilterSelect, {
  FilterDatePill,
  FilterValuePill,
  humanizeFilterParam,
  labelForFilterParam,
  isTimeWindowFilter,
  shouldPreferNativeFilterSelect,
  formatDynamicFilterChipValue,
} from './DynamicFilterSelect';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SettingsIcon from '@mui/icons-material/Settings';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import ChartWidget from './ChartWidget';
import WeatherWidget, { weatherRowsForExport } from './WeatherWidget';
import CalendarWidget, { calendarRowsForExport } from './CalendarWidget';
import CalendarEventsEditor from './CalendarEventsEditor';
import ButtonWidget, { buttonRowsForExport } from './ButtonWidget';
import { resolveTableColumnLabels } from '../utils/tableColumnLabels';
import {
  resolveXDateBucket,
  resolveXDateDisplayFormat,
  resolveLabelDateBucket,
  resolveLabelDateDisplayFormat,
} from '../utils/dateBucketFormat';
import { resolveEpisodeDurationPresentation } from '../utils/episodeOutputMeta';
import DataTable from './DataTable';
import ChartAdvancedConfig from './ChartAdvancedConfig';
import { buildParamsForChart as buildParamsForChartUtil, getChartDynamicFilters, defaultGridLayoutItem, withGridLayoutConstraints, stackLayoutForNarrowViewport, shouldStackDashboardLayout } from '../utils/dashboardData';
import {
  buildSiblingParamsForFilter,
  canUseCascadeOptions,
  mapQueryResultToOptions,
  optionsContextKey,
  pickValidFilterValue,
  sortChartDynamicFilters,
  withOptionalAnyOption,
} from '../utils/dynamicFilterOptions';
import {
  applyCommittedHeatmapPositionColumns,
  resolveCommittedHeatmapPositions,
  resolveImageDensityConfig,
} from '../utils/imageDensityConfig';
import { appendHeatmapPositionVisibleColumns } from '../utils/imageDensityPoints';
import { resolveImageCarouselConfig } from '../utils/imageCarouselConfig';
import { resolveGeoHeatmapConfig } from '../utils/geoHeatmapConfig';
import { temporalMapsFromColumns } from '../utils/tooltipDetail';
import TablePlusView from './TablePlusView';
import { resolvePlainColumnAliases } from './chartBuilder/LiveDatasetTable';
import { useRequestLeave } from '../contexts/UnsavedSessionContext';
import { downloadChartExcel } from '../utils/excelExport';
import { applyCalculatedColumns, isCalculatedColumnKey, normalizeCalculatedColumns, sumCalculatedColumn } from '../utils/calculatedColumns';
import {
  dateBucketPivotConfigForChart as resolveDateBucketPivotConfigForChart,
  pivotRowsByDateBucket,
  dateBucketPivotColumnOrder,
} from '../utils/pivotDateBucketTable';
import { getChartPeriodCaption, kpiCompactExportNeedsTallTile } from '../utils/chartTilePeriodCaption';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';

const CALC_AXIS_CHART_TYPES = new Set(['bar', 'line', 'pie', 'doughnut', 'radar', 'kpi']);

/** La preview del constructor usa el mapping de la consulta (alias reales de las filas).
 *  El chart guardado puede tener xColumn/yColumn de una publicación anterior, cuando el
 *  eje era otra columna. Si un eje es calculado, el mapping que viaja con los datos es
 *  la misma fuente que la preview. */
function calculatedAxisMapping(chart, dataForKey) {
  const mapping = dataForKey?.mapping;
  if (!mapping || !CALC_AXIS_CHART_TYPES.has(chart?.type)) return null;
  const defs =
    chart.advancedConfig?.tableCalculatedColumns ||
    chart.visualizationSpec?.visualization?.tableCalculatedColumns ||
    [];
  if (!Array.isArray(defs) || !defs.length) return null;
  const viz = chart.visualizationSpec?.visualization || {};
  const fields = [
    mapping.xAlias,
    mapping.yAlias,
    mapping.labelAlias,
    viz.xField,
    viz.yField,
    viz.seriesField,
    chart.xColumn,
    chart.yColumn,
    chart.labelColumn,
  ];
  if (!fields.some((field) => isCalculatedColumnKey(field))) return null;
  if (mapping.xDateBucket || mapping.xDateDisplayFormat) return mapping;
  const cols = chart.visualizationSpec?.tableColumns;
  const aliases = Array.isArray(cols) ? resolvePlainColumnAliases(cols) : [];
  const idx = aliases.findIndex((alias) => alias === mapping.xAlias || alias === viz.xField);
  const col = idx >= 0 ? cols[idx] : null;
  if (!col) return mapping;
  const dateBucket = col.dateBucket && col.dateBucket !== 'raw' ? col.dateBucket : undefined;
  const dateDisplayFormat =
    col.dateDisplayFormat && col.dateDisplayFormat !== 'auto' ? col.dateDisplayFormat : undefined;
  return {
    ...mapping,
    ...(dateBucket ? { xDateBucket: dateBucket } : {}),
    ...(dateDisplayFormat ? { xDateDisplayFormat: dateDisplayFormat } : {}),
  };
}

function imageDensityConfigWithDates(chart, data) {
  const config = resolveImageDensityConfig(chart, data);
  const spec = chart?.visualizationSpec || {};
  const columns = spec.tableColumns || [];
  const maps = temporalMapsFromColumns(columns, resolvePlainColumnAliases(columns), {
    episodeDateFormat:
      spec.visualization?.chartFeed === 'episodes' ? spec.episodeAnalysis?.xDateDisplayFormat : undefined,
  });
  return {
    ...config,
    columnDateFormats: { ...(config.columnDateFormats || {}), ...maps.columnDateFormats },
    columnDateBuckets: { ...(config.columnDateBuckets || {}), ...maps.columnDateBuckets },
  };
}

/**
 * Config del pivote del gráfico (una columna de agrupación en filas, un
 * bucket de fecha ciclico en columnas — hora del día, día de la semana, día
 * del mes o mes), o null si no aplica. Funcion pura y explicita para que
 * tanto el render de la tabla como el export a Excel del tile pivoteen de la
 * misma forma, sin depender de que otro codigo haya mutado chartData antes.
 * La logica en si vive en pivotDateBucketTable.js (sin dependencias de React
 * ni de otros componentes) para que sea testeable de forma aislada.
 */
export function dateBucketPivotConfigForChart(chart, rows) {
  if (chart?.type !== 'table' || !Array.isArray(chart?.visualizationSpec?.tableColumns)) return null;
  const tableColumns = chart.visualizationSpec.tableColumns;
  return resolveDateBucketPivotConfigForChart(tableColumns, resolvePlainColumnAliases(tableColumns), rows);
}

/** Filas que se deben mostrar/exportar para un tile de tabla: pivoteadas si corresponde, si no, tal cual. */
export function tableRowsForDisplay(chart, rawRows) {
  const pivotConfig = dateBucketPivotConfigForChart(chart, rawRows);
  return pivotConfig ? pivotRowsByDateBucket(rawRows, pivotConfig) : rawRows;
}

const CHART_TYPES = [
  { value: 'line', label: 'Línea' },
  { value: 'bar', label: 'Barras' },
  { value: 'pie', label: 'Pastel' },
  { value: 'doughnut', label: 'Dona' },
  { value: 'radar', label: 'Radar (spider)' },
  { value: 'scatter', label: 'Dispersión' },
  { value: 'intervals', label: 'Intervalos' },
  { value: 'heatmapGrid', label: 'Heatmap sobre imagen' },
  { value: 'imageDensity', label: 'Densidad sobre imagen' },
  { value: 'imageCarousel', label: 'Carrusel de imágenes' },
  { value: 'geoHeatmap', label: 'Mapa de calor (geo)' },
  { value: 'map', label: 'Mapa' },
  { value: 'table', label: 'Tabla' },
  { value: 'tablePlus', label: 'Tabla+' },
];

// Misma forma que en ChartEditDialog para que el dashboard renderice igual que la preview
const DEFAULT_ADVANCED_CONFIG = {
  title: '',
  xAxisTitle: '',
  yAxisTitle: '',
  legendPosition: 'top',
  legendDisplay: true,
  gridLines: true,
  xAxisMin: '',
  xAxisMax: '',
  yAxisMin: '',
  yAxisMax: '',
  pointRadius: '',
  borderWidth: '',
  seriesColors: {},
  seriesColorIndex: {},
  tableShowTotalColumn: false,
  tableShowTotalRow: false,
  tableColumnFilters: {},
  barShowTotalLabel: false,
  barShowSegmentLabel: false,
  lineShowValueLabel: false,
  lineFillMissingAsZero: true,
  fillTimeAxisGaps: false,
  lineInterpolation: 'linear',
  dynamicFilter: { enabled: false, paramName: '', filterType: 'select', optionSource: 'query', optionsQuery: '', labelColumn: '', valueColumn: '', staticOptions: [] },
  dynamicFilters: [],
};

// Fechas ISO en chips de filtro: solo DD-MM-YYYY (la consulta sigue usando el valor completo).
const kpiFilterChipRowSx = {
  mb: 0.35,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 0.35,
  flexWrap: 'wrap',
  minWidth: 0,
  width: '100%',
};

const filterPillRowSx = {
  mb: 0.75,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  flexWrap: 'wrap',
  minWidth: 0,
  width: '100%',
};

const tilePeriodCaptionRowSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.35,
  maxWidth: '100%',
  mb: 0.35,
  flexShrink: 0,
  color: 'text.secondary',
};

const tilePeriodCaptionTextSx = {
  fontSize: '0.6875rem',
  lineHeight: 1.2,
  fontWeight: 500,
};

/** Fila compacta de chips solo si todos los filtros visibles son chips readonly (no date/select). */
function kpiTileFilterRowIsReadonlyChipsOnly(chart, isParamGlobal, hasRunQuery) {
  if (chart?.type !== 'kpi') return false;
  const visible = getChartDynamicFilters(chart).filter((f) => f?.enabled && f?.paramName);
  if (visible.length === 0) return false;
  return visible.every((df) => {
    const paramName = String(df.paramName).trim();
    if (isParamGlobal(paramName)) return true;
    if (hasRunQuery) return false;
    return true;
  });
}

const ChartGrid = ({ queryData, queryDataByQueryId = {}, getQueryDataKey, getAuthHeaders, queryMetaByQueryId = {}, runQueryWithParams, runOptionsQuery, runFilterOptionsQuery, onDynamicFilterChange, globalFilterParamNames = [], globalFilterValues = {}, onGlobalFilterParamNamesChange, onGlobalFilterValuesChange, globalFilterParamAliases = {}, chartsDataLoading = false, charts: externalCharts, onChartsChange, query, onQueryExecute, selectedDatabase, selectedConnectionId = '', savedQueries = [], selectedQueryId, onQuerySelect, layout: externalLayout, onLayoutChange, dashboardName, onOpenChartBuilder, onEditChart, onHideChart, onRemoveChart, onShowChartOnGrid, onMakeInstanceIndependent, currentDashboardId, highlightChartId, sharedOriginalIds, chartLibrary = [], hiddenCharts = [], printCapture = false, embedMode = false, readOnlyMode = false, presentationMode = false, weatherDataByChartId = {}, onWeatherDataLoaded, dashboardTimezone }) => {
  const [charts, setCharts] = useState(externalCharts || []);
  const [advancedConfigOpen, setAdvancedConfigOpen] = useState(false);
  const [selectedChartForConfig, setSelectedChartForConfig] = useState(null);
  const [hiddenChartsOpen, setHiddenChartsOpen] = useState(true);
  const [forkingChartId, setForkingChartId] = useState(null);
  const chartHighlightRef = useRef(null);
  // Gráfico de calendario: un solo popover reutilizado para editar la lista de
  // feriados/eventos de cualquier tile de calendario (dato del widget, no
  // configuracion del chart; eso ultimo ahora va por el editor de graficos normal,
  // igual que clima y boton).
  const [calendarSettingsFor, setCalendarSettingsFor] = useState(null);
  // Filtro dinámico: opciones y valor seleccionado por chart.id + paramName
  const [dynamicFilterOptions, setDynamicFilterOptions] = useState({});
  const [dynamicFilterValue, setDynamicFilterValue] = useState({});
  const [addGlobalAnchor, setAddGlobalAnchor] = useState(null);
  const dynamicFilterLoadingRef = useRef(new Set());
  const chartQueryLoadingRef = useRef(new Set());

  // Reintentar cargar opciones si el primer intento falló antes de tener connectionId/database.
  useEffect(() => {
    const hasConn = Boolean(selectedConnectionId) || charts.some((c) => c?.connectionId || queryMetaByQueryId[c?.queryId]?.connectionId);
    if (!hasConn) return;
    setDynamicFilterOptions((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [key, opts] of Object.entries(prev)) {
        if (Array.isArray(opts) && opts.length === 0) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedConnectionId, queryMetaByQueryId, charts]);
  // Opciones para cada filtro global (paramName -> options[])
  const [globalFilterOptions, setGlobalFilterOptions] = useState({});
  const globalFilterLoadingRef = useRef(new Set());
  const [refreshingChartIds, setRefreshingChartIds] = useState(() => new Set());
  const requestLeave = useRequestLeave();

  const handleRefreshChart = async (chart, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!chart?.queryId || !runQueryWithParams) return;
    const chartId = chart.id;
    requestLeave(async () => {
      setRefreshingChartIds((prev) => {
        const next = new Set(prev);
        next.add(chartId);
        return next;
      });
      try {
        await runQueryWithParams(chart.queryId, buildParamsForChart(chart), chart);
      } finally {
        setRefreshingChartIds((prev) => {
          const next = new Set(prev);
          next.delete(chartId);
          return next;
        });
      }
    });
  };

  const chartFilterEntries = useMemo(() => {
    const entries = [];
    charts.forEach((chart) => {
      if (!chart?.queryId) return;
      const filters = sortChartDynamicFilters(getChartDynamicFilters(chart));
      filters.forEach((df) => {
        const paramName = String(df?.paramName || '').trim();
        if (!df?.enabled || !paramName) return;
        entries.push({
          chart,
          df,
          paramName,
          optionKey: `${chart.id}::${paramName}`,
        });
      });
    });
    entries.sort((a, b) => (a.df.filterOrder ?? 0) - (b.df.filterOrder ?? 0));
    return entries;
  }, [charts]);

  const getSiblingParams = (chart, paramName) =>
    buildSiblingParamsForFilter(chart, paramName, {
      globalFilterValues,
      globalFilterParamNames,
      getLocalValue: (ch, p) => {
        const df = getChartDynamicFilters(ch).find(
          (f) => String(f?.paramName || '').trim() === String(p).trim()
        );
        const raw = dynamicFilterValueRef.current[`${ch.id}::${p}`] ?? df?.selectedValue;
        return df?.filterType === 'date' && typeof raw === 'string' ? raw.slice(0, 10) : raw;
      },
    });

  const resolveFilterOptions = (optionKey, chart, paramName) => {
    const ctxKey = optionsContextKey(optionKey, getSiblingParams(chart, paramName));
    const raw = dynamicFilterOptions[ctxKey] || dynamicFilterOptions[optionKey] || [];
    const df = getChartDynamicFilters(chart).find(
      (f) => String(f?.paramName || '').trim() === String(paramName || '').trim()
    );
    return withOptionalAnyOption(raw, df?.allowExecuteWithoutSelection === true);
  };

  const resolveGlobalFilterOptions = (paramName) => {
    const siblingParams = {};
    (globalFilterParamNames || []).forEach((p) => {
      const key = String(p).trim();
      if (!key || key === String(paramName).trim()) return;
      const v = globalFilterValues[key];
      if (v !== undefined && v !== null && String(v).trim() !== '') siblingParams[key] = v;
    });
    const ctxKey = optionsContextKey(`global::${paramName}`, siblingParams);
    const raw = globalFilterOptions[ctxKey] || globalFilterOptions[paramName] || [];
    const df = chartFilterEntries.find((e) => e.paramName === paramName)?.df;
    return withOptionalAnyOption(raw, df?.allowExecuteWithoutSelection === true);
  };

  const availableParamNames = useMemo(() => {
    const set = new Set();
    chartFilterEntries.forEach(({ paramName }) => {
      if (paramName) set.add(String(paramName).trim());
    });
    return Array.from(set);
  }, [chartFilterEntries]);

  const isParamGlobal = (paramName) => (globalFilterParamNames || []).includes(String(paramName).trim());

  const resolveChartDatabase = (chart) =>
    queryMetaByQueryId[chart?.queryId]?.database || chart?.database || selectedDatabase;

  const resolveChartConnectionId = (chart) =>
    queryMetaByQueryId[chart?.queryId]?.connectionId || chart?.connectionId || selectedConnectionId;

  const dynamicFilterValueRef = useRef({});
  dynamicFilterValueRef.current = dynamicFilterValue;

  // Misma lógica que ReportPreview (utils/dashboardData) para que dashboard y previsualización coincidan.
  const buildParamsForChart = (chart, overrides = {}) =>
    buildParamsForChartUtil(chart, {
      globalFilterValues,
      globalFilterParamAliases,
      globalFilterParamNames,
      overrides,
      getDynamicFilterValue: (ch, paramName) =>
        overrides[paramName] ??
        dynamicFilterValueRef.current[`${ch?.id}::${paramName}`] ??
        getChartDynamicFilters(ch).find((f) => String(f?.paramName || '').trim() === String(paramName || '').trim())?.selectedValue,
    });

  const runChartQueryIfMissing = (chart, params) => {
    if (!chart?.queryId || !runQueryWithParams) return;
    const dataKey = typeof getQueryDataKey === 'function'
      ? getQueryDataKey(chart.queryId, params, chart)
      : chart.queryId;
    // No se revisa queryDataByQueryId aqui: la prop puede estar desactualizada en el mismo ciclo de
    // efectos. runQueryWithParams con soloSiFalta ya devuelve el resultado cacheado o comparte la
    // peticion en vuelo, sin volver a consultar el servidor.
    const loadingKey = String(dataKey ?? chart.queryId);
    if (chartQueryLoadingRef.current.has(loadingKey)) return;
    chartQueryLoadingRef.current.add(loadingKey);
    Promise.resolve(runQueryWithParams(chart.queryId, params, chart, { soloSiFalta: true })).finally(() => {
      chartQueryLoadingRef.current.delete(loadingKey);
    });
  };

  // Cargar opciones para filtros globales (un origen por param: primer gráfico que lo use)
  useEffect(() => {
    if (!runOptionsQuery || !runQueryWithParams || !onGlobalFilterValuesChange) return;
    (globalFilterParamNames || []).forEach(paramName => {
      const firstEntry = chartFilterEntries.find(
        ({ paramName: p }) => String(p).trim() === String(paramName).trim()
      );
      if (!firstEntry) return;
      const { chart: firstChart, df } = firstEntry;
      if (df.filterType === 'date') {
        const currentVal = globalFilterValues[paramName];
        if (currentVal === undefined || currentVal === null || String(currentVal).trim() === '') {
          const normalized = df.selectedValue && String(df.selectedValue).trim() !== ''
            ? String(df.selectedValue).slice(0, 10)
            : new Date().toISOString().slice(0, 10);
          onGlobalFilterValuesChange(prev => ({ ...prev, [paramName]: normalized }));
        }
        return;
      }
      const hasQuery = (df.optionsQuery || '').trim() && df.valueColumn && df.labelColumn;
      const hasStatic = df.optionSource === 'static' && Array.isArray(df.staticOptions) && df.staticOptions.length > 0;

      if (hasStatic && !hasQuery) {
        if (globalFilterOptions[paramName] !== undefined) return;
        const trimIfString = (v) => (v != null && typeof v === 'string' ? v.trim() : v);
        const options = df.staticOptions.map((o, i) => ({
          label: String(o.label ?? o.value ?? '').trim(),
          value: trimIfString(o.value),
          _key: `static-${paramName}-${i}`,
        }));
        setGlobalFilterOptions(prev => ({ ...prev, [paramName]: options }));
        const currentVal = globalFilterValues[paramName];
        const allowEmpty = df.allowExecuteWithoutSelection === true;
        const withAny = withOptionalAnyOption(options, allowEmpty);
        const firstVal = pickValidFilterValue(currentVal, df.selectedValue, withAny, { allowEmpty });
        if (firstVal !== undefined && String(firstVal) !== String(currentVal ?? '')) {
          onGlobalFilterValuesChange(prev => ({ ...prev, [paramName]: firstVal }));
        } else if (firstVal !== undefined && (currentVal === undefined || currentVal === null)) {
          onGlobalFilterValuesChange(prev => ({ ...prev, [paramName]: firstVal }));
        }
        return;
      }

      const canCascade = canUseCascadeOptions(firstChart, df) && typeof runFilterOptionsQuery === 'function';
      if (!canCascade && !hasQuery) return;

      const siblingParams = {};
      (globalFilterParamNames || []).forEach((p) => {
        const key = String(p).trim();
        if (!key || key === String(paramName).trim()) return;
        const v = globalFilterValues[key];
        if (v !== undefined && v !== null && String(v).trim() !== '') siblingParams[key] = v;
      });
      const ctxKey = optionsContextKey(`global::${paramName}`, siblingParams);
      if (globalFilterOptions[ctxKey] !== undefined) {
        const cached = globalFilterOptions[ctxKey];
        const currentVal = globalFilterValues[paramName];
        const allowEmpty = df.allowExecuteWithoutSelection === true;
        const withAny = withOptionalAnyOption(cached, allowEmpty);
        if (withAny?.length) {
          const nextVal = pickValidFilterValue(currentVal, null, withAny, { allowEmpty });
          if (nextVal !== undefined && String(nextVal) !== String(currentVal ?? '')) {
            onGlobalFilterValuesChange((prev) => ({ ...prev, [paramName]: nextVal }));
          } else if (nextVal !== undefined && (currentVal === undefined || currentVal === null)) {
            onGlobalFilterValuesChange((prev) => ({ ...prev, [paramName]: nextVal }));
          }
        }
        return;
      }
      if (globalFilterLoadingRef.current.has(ctxKey)) return;
      globalFilterLoadingRef.current.add(ctxKey);
      const db = resolveChartDatabase(firstChart);
      const connId = resolveChartConnectionId(firstChart);
      const fetchPromise = canCascade
        ? runFilterOptionsQuery(firstChart, paramName, siblingParams, db, connId)
        : runOptionsQuery(df.optionsQuery.trim(), db, connId);

      fetchPromise.then((result) => {
        globalFilterLoadingRef.current.delete(ctxKey);
        const options = mapQueryResultToOptions(result, df, `global-${ctxKey}`);
        setGlobalFilterOptions((prev) => ({ ...prev, [ctxKey]: options }));
        const currentVal = globalFilterValues[paramName];
        const allowEmpty = df.allowExecuteWithoutSelection === true;
        const withAny = withOptionalAnyOption(options, allowEmpty);
        const firstVal = pickValidFilterValue(currentVal, df.selectedValue, withAny, { allowEmpty });
        if (firstVal !== undefined && String(firstVal) !== String(currentVal ?? '')) {
          onGlobalFilterValuesChange((prev) => ({ ...prev, [paramName]: firstVal }));
        } else if (firstVal !== undefined && (currentVal === undefined || currentVal === null)) {
          onGlobalFilterValuesChange((prev) => ({ ...prev, [paramName]: firstVal }));
        }
      }).catch(() => {
        globalFilterLoadingRef.current.delete(ctxKey);
        setGlobalFilterOptions((prev) => ({ ...prev, [ctxKey]: [] }));
      });
    });
  }, [
    chartFilterEntries,
    globalFilterParamNames,
    runOptionsQuery,
    runFilterOptionsQuery,
    queryMetaByQueryId,
    selectedDatabase,
    selectedConnectionId,
    globalFilterOptions,
    globalFilterValues,
    onGlobalFilterValuesChange,
    runQueryWithParams,
  ]);

  // Al cambiar filtros globales (o al añadir/quitar un gráfico), re-ejecutar los gráficos con queryId. NO re-ejecutar cuando solo cambia el valor de un filtro por gráfico (charts cambia por onDynamicFilterChange), para no pisar la petición del onChange.
  const allChartsWithQueryRef = useRef([]);
  allChartsWithQueryRef.current = (externalCharts ?? charts).filter(c => c && c.queryId);
  const chartIdsWithQueryJoin = (externalCharts ?? charts).filter(c => c && c.queryId).map(c => c.id).join(',');
  const prevGlobalCountRef = useRef((globalFilterParamNames || []).length);
  useEffect(() => {
    if (!runQueryWithParams) return;
    const count = (globalFilterParamNames || []).length;
    const hadGlobal = prevGlobalCountRef.current > 0;
    const hasGlobal = count > 0;
    prevGlobalCountRef.current = count;
    if (!hasGlobal && !hadGlobal) return;
    allChartsWithQueryRef.current.forEach(chart => {
      const localFilters = getChartDynamicFilters(chart).filter(
        (f) => f?.enabled && f?.paramName && !isParamGlobal(f.paramName)
      );
      const missingLocalRequired = localFilters.some((f) => {
        const key = `${chart.id}::${String(f.paramName).trim()}`;
        const v = dynamicFilterValueRef.current[key] ?? f.selectedValue;
        return f.allowExecuteWithoutSelection !== true && (v === undefined || v === null || String(v).trim() === '');
      });
      if (missingLocalRequired) return;
      const missingGlobalRequired = (globalFilterParamNames || []).some((paramName) => {
        const df = getChartDynamicFilters(chart).find(
          (f) => String(f?.paramName || '').trim() === String(paramName).trim()
        );
        if (!df?.enabled) return false;
        const v = globalFilterValues[paramName];
        return df.allowExecuteWithoutSelection !== true && (v === undefined || v === null || String(v).trim() === '');
      });
      if (missingGlobalRequired) return;
      runQueryWithParams(chart.queryId, buildParamsForChart(chart), chart, { soloSiFalta: true });
    });
  }, [globalFilterParamNames, globalFilterValues, runQueryWithParams, chartIdsWithQueryJoin]);

  // Cargar opciones del filtro dinámico por gráfico (cascada entre filtros de datos) y ejecutar query
  useEffect(() => {
    if (!runQueryWithParams) return;
    chartFilterEntries.forEach(({ chart, df, paramName, optionKey }) => {
      if (isParamGlobal(paramName)) return;
      if (df.filterType === 'date') {
        const currentVal = dynamicFilterValue[optionKey] ?? df.selectedValue;
        const normalized = currentVal != null && String(currentVal).trim() !== ''
          ? String(currentVal).slice(0, 10)
          : new Date().toISOString().slice(0, 10);
        if (dynamicFilterValue[optionKey] == null || String(dynamicFilterValue[optionKey]).trim() === '') {
          setDynamicFilterValue(prev => ({ ...prev, [optionKey]: normalized }));
        }
        runChartQueryIfMissing(chart, buildParamsForChart(chart, { [paramName]: normalized }));
        return;
      }

      const hasStatic = df.optionSource === 'static' && Array.isArray(df.staticOptions) && df.staticOptions.length > 0;
      const hasQuery = (df.optionsQuery || '').trim() && df.valueColumn && df.labelColumn;
      const canCascade = canUseCascadeOptions(chart, df) && typeof runFilterOptionsQuery === 'function';

      if (hasStatic && !hasQuery && !canCascade) {
        const trimIfString = (v) => (v != null && typeof v === 'string' ? v.trim() : v);
        const options = dynamicFilterOptions[optionKey] !== undefined
          ? dynamicFilterOptions[optionKey]
          : df.staticOptions.map((o, i) => ({
              label: String(o.label ?? o.value ?? '').trim(),
              value: trimIfString(o.value),
              _key: `chart-${optionKey}-static-${i}`,
            }));
        if (dynamicFilterOptions[optionKey] === undefined) {
          setDynamicFilterOptions(prev => ({ ...prev, [optionKey]: options }));
        }
        const allowEmpty = df.allowExecuteWithoutSelection === true;
        const withAny = withOptionalAnyOption(options, allowEmpty);
        const savedVal = df.selectedValue;
        const firstVal = pickValidFilterValue(dynamicFilterValue[optionKey], savedVal, withAny, { allowEmpty });
        if (firstVal !== undefined) {
          if (String(dynamicFilterValue[optionKey] ?? '') !== String(firstVal)) {
            setDynamicFilterValue(prev => ({ ...prev, [optionKey]: firstVal }));
          }
          runChartQueryIfMissing(chart, buildParamsForChart(chart, { [paramName]: firstVal }));
        }
        return;
      }

      if (!canCascade && (!hasQuery || !runOptionsQuery)) return;

      const siblingParams = getSiblingParams(chart, paramName);
      const ctxKey = optionsContextKey(optionKey, siblingParams);
      if (dynamicFilterOptions[ctxKey] !== undefined) {
        const options = dynamicFilterOptions[ctxKey];
        const allowEmpty = df.allowExecuteWithoutSelection === true;
        const withAny = withOptionalAnyOption(options, allowEmpty);
        if (withAny?.length) {
          const nextVal = pickValidFilterValue(
            dynamicFilterValueRef.current[optionKey],
            df.selectedValue,
            withAny,
            { allowEmpty }
          );
          if (nextVal !== undefined) {
            if (String(dynamicFilterValueRef.current[optionKey] ?? '') !== String(nextVal)) {
              setDynamicFilterValue(prev => ({ ...prev, [optionKey]: nextVal }));
            }
            runChartQueryIfMissing(chart, buildParamsForChart(chart, { [paramName]: nextVal }));
          }
        }
        return;
      }
      if (dynamicFilterLoadingRef.current.has(ctxKey)) return;
      dynamicFilterLoadingRef.current.add(ctxKey);

      const db = resolveChartDatabase(chart);
      const connId = resolveChartConnectionId(chart);
      const fetchPromise = canCascade
        ? runFilterOptionsQuery(chart, paramName, siblingParams, db, connId)
        : runOptionsQuery(df.optionsQuery.trim(), db, connId);

      fetchPromise.then((result) => {
        dynamicFilterLoadingRef.current.delete(ctxKey);
        const options = mapQueryResultToOptions(result, df, `chart-${ctxKey}`);
        setDynamicFilterOptions(prev => ({ ...prev, [ctxKey]: options }));
        const allowEmpty = df.allowExecuteWithoutSelection === true;
        const withAny = withOptionalAnyOption(options, allowEmpty);
        if (!withAny.length) return;

        const savedVal = df.selectedValue;
        const currentVal = dynamicFilterValueRef.current[optionKey];
        const nextVal = pickValidFilterValue(currentVal, savedVal, withAny, { allowEmpty });
        if (nextVal === undefined) return;

        const valueChanged = String(nextVal) !== String(currentVal ?? '');
        if (valueChanged) {
          setDynamicFilterValue(prev => ({ ...prev, [optionKey]: nextVal }));
        }
        runChartQueryIfMissing(chart, buildParamsForChart(chart, { [paramName]: nextVal }));
      }).catch(() => {
        dynamicFilterLoadingRef.current.delete(ctxKey);
        setDynamicFilterOptions(prev => ({ ...prev, [ctxKey]: [] }));
      });
    });
  }, [
    chartFilterEntries,
    runOptionsQuery,
    runFilterOptionsQuery,
    runQueryWithParams,
    queryMetaByQueryId,
    selectedDatabase,
    selectedConnectionId,
    dynamicFilterOptions,
    dynamicFilterValue,
    globalFilterValues,
    globalFilterParamNames,
    queryDataByQueryId,
    getQueryDataKey,
  ]);

  // Scroll al gráfico recién actualizado y quitar highlight tras animación
  useEffect(() => {
    if (highlightChartId && chartHighlightRef.current) {
      chartHighlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightChartId]);

  // Sincronizar charts externos con estado interno
  useEffect(() => {
    if (externalCharts !== undefined) {
      setCharts(externalCharts);
    }
  }, [externalCharts]);
  
  // Notificar cambios al componente padre
  const updateCharts = (newCharts) => {
    setCharts(newCharts);
    if (onChartsChange) {
      onChartsChange(newCharts);
    }
  };
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);

  // Ancho real del contenedor (ResizeObserver + fallback resize)
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const computedStyle = window.getComputedStyle(node);
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
      const width = node.offsetWidth - paddingLeft - paddingRight;
      setContainerWidth(width > 0 ? width : 0);
    };

    updateWidth();
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateWidth);
      observer.observe(node);
    }
    window.addEventListener('resize', updateWidth);
    const timeoutId = setTimeout(updateWidth, 100);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timeoutId);
    };
  }, [charts.length]);

  // Comparar id de chart con item.i (pueden venir number/string desde el servidor)
  const layoutItemMatchesChart = (item, chart) =>
    chart.id === item.i || String(chart.id) === String(item.i) || Number(chart.id) === Number(item.i);

  const normalizeLayoutItem = (item, chart) =>
    chart ? withGridLayoutConstraints({ ...item, i: chart.id }, chart.type) : item;

  // Generar layout para react-grid-layout
  // Si hay un layout externo, usarlo y agregar items para nuevos charts; si no, generarlo automáticamente
  const layout = useMemo(() => {
    if (externalLayout && externalLayout.length > 0) {
      // Filtrar el layout para incluir solo los charts que existen (comparación por valor, no solo referencia)
      const filtered = externalLayout.filter(item => charts.some(chart => layoutItemMatchesChart(item, chart)));
      // Normalizar item.i al chart.id real para que coincida con key={chart.id} en el grid
      const existingLayout = filtered.map((item) => {
        const chart = charts.find((chart) => layoutItemMatchesChart(item, chart));
        return chart ? normalizeLayoutItem(item, chart) : item;
      });
      
      // Encontrar charts que no tienen layout y agregarlos
      const chartsWithoutLayout = charts.filter(chart => !existingLayout.some(item => item.i === chart.id));
      
      if (chartsWithoutLayout.length > 0) {
        // Agregar nuevos items al layout
        const maxY = existingLayout.length > 0 
          ? Math.max(...existingLayout.map(item => item.y + item.h))
          : 0;
        
        const newLayoutItems = chartsWithoutLayout.map((chart, index) =>
          defaultGridLayoutItem(chart.id, chart.type, {
            x: 0,
            y: maxY + index * 4,
            w: 12,
            h: 4,
          })
        );
        
        return [...existingLayout, ...newLayoutItems];
      }
      
      return existingLayout;
    }
    // Generar layout automático si no hay uno externo
    return charts.map((chart, index) =>
      defaultGridLayoutItem(chart.id, chart.type, {
        x: 0,
        y: index * 4,
        w: 12,
        h: 4,
      })
    );
  }, [charts, externalLayout]);

  const isNarrowViewport = shouldStackDashboardLayout(containerWidth);
  const gridLayout = useMemo(
    () => (isNarrowViewport ? stackLayoutForNarrowViewport(layout, charts) : layout),
    [layout, charts, isNarrowViewport]
  );
  // En presentacion, achica solo visualmente los tiles para recuperar el aire que dejan los
  // elementos que se ocultan (icono/caption/filtros dinamicos): los KPI van a su alto minimo
  // (su contenido real es mucho mas bajo), y cualquier grafico o tabla con filtros dinamicos
  // habilitados baja 1 fila (lo que ocupaba esa franja de filtro ya oculta). El resto de los
  // tiles sube para llenar el hueco gracias a compactType="vertical". No se persiste: es un
  // PDF: layout compacto completo. Presentación: achica solo KPI con periodo/YoY; KPI
  // simples (ej. ultima lectura) conservan el h guardado en edición.
  const compactExportTiles = printCapture;
  const displayLayout = useMemo(() => {
    return gridLayout.map((item) => {
      const chart = charts.find((c) => layoutItemMatchesChart(item, c));
      if (!chart) return item;
      const minH = item.minH || 3;
      if (chart.type === 'kpi') {
        if (compactExportTiles) {
          const tallKpi = kpiCompactExportNeedsTallTile(chart);
          const targetH = isNarrowViewport ? (tallKpi ? 4 : 3) : tallKpi ? 3 : 2;
          return { ...item, h: targetH, minH: targetH };
        }
        if (presentationMode && kpiCompactExportNeedsTallTile(chart)) {
          const targetH = isNarrowViewport ? 4 : 3;
          if (item.h > targetH) {
            return { ...item, h: targetH, minH: Math.min(minH, targetH) };
          }
          return item;
        }
        if (presentationMode) {
          return item;
        }
        return item;
      }
      // Presentación: conservar h del layout (solo KPI achica arriba). Restar 1 fila aqui
      // recortaba barras/lineas (titulo del eje X) sin ganar espacio real equivalente.
      if (!compactExportTiles) return item;
      const hasHiddenDynamicFilters = getChartDynamicFilters(chart).some((f) => f?.enabled && f?.paramName);
      // Tablas y ejes: restar h recorta contenido (paginación, titulo eje X) sin ganancia clara.
      const skipPrintRowCompact =
        chart.type === 'table' || chart.type === 'tablePlus' || chart.type === 'line' || chart.type === 'bar' || chart.type === 'scatter';
      if (hasHiddenDynamicFilters && !skipPrintRowCompact) {
        const compactH = Math.max(minH, item.h - 1);
        return item.h > compactH ? { ...item, h: compactH } : item;
      }
      return item;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLayout, charts, compactExportTiles, presentationMode, isNarrowViewport]);

  // En edición el layout guardado debe gobernar el resize; displayLayout solo compacta
  // presentación / PDF y no debe imponer minH ni cap de h distinto al persistido.
  const layoutForGrid = presentationMode || compactExportTiles ? displayLayout : gridLayout;

  const gridRowHeight = isNarrowViewport ? 52 : 60;
  const layoutLocked = printCapture || isNarrowViewport || readOnlyMode || embedMode;
  // Impresión, embed y celular dejan crecer la página para que scrollee el contenedor de afuera.
  // Solo lectura solo bloquea arrastrar: en escritorio el padre tiene overflow hidden, así que
  // el scroll vertical tiene que seguir en esta grilla.
  const contentGrowsWithPage = printCapture || isNarrowViewport || embedMode;
  const preferNativeFilterSelect = shouldPreferNativeFilterSelect({
    embedMode,
    narrowViewport: isNarrowViewport,
  });

  useEffect(() => {
    if (containerWidth <= 0) return undefined;
    const timeoutId = setTimeout(() => window.dispatchEvent(new Event('resize')), 120);
    return () => clearTimeout(timeoutId);
  }, [containerWidth, gridLayout]);

  // Embed (WebView directo): el layout tarda en estabilizarse; forzar redraw de heatmaps/canvas.
  useEffect(() => {
    if (!embedMode) return undefined;
    document.documentElement.classList.add('embed-view');
    return () => document.documentElement.classList.remove('embed-view');
  }, [embedMode]);

  useEffect(() => {
    if (!embedMode || charts.length === 0) return undefined;
    const t1 = setTimeout(() => window.dispatchEvent(new Event('resize')), 80);
    const t2 = setTimeout(() => window.dispatchEvent(new Event('resize')), 350);
    const t3 = setTimeout(() => window.dispatchEvent(new Event('resize')), 900);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [embedMode, charts.length, containerWidth]);

  const handleRemoveFromGrid = (chartId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    requestLeave(() => {
      if (onHideChart) {
        onHideChart(chartId);
        return;
      }
      const updatedCharts = charts.filter((c) => c.id !== chartId);
      updateCharts(updatedCharts);
      if (onLayoutChange && externalLayout) {
        const updatedLayout = externalLayout.filter((item) => item.i !== chartId);
        onLayoutChange(updatedLayout);
      }
    });
  };

  // Layouts viejos guardaron minH 3–4 en KPI; bajar a 2 en estado para que RGL permita achicar.
  useEffect(() => {
    if (layoutLocked || presentationMode || !onLayoutChange || !externalLayout?.length) return;
    let dirty = false;
    const updated = externalLayout.map((item) => {
      const chart = charts.find((c) => layoutItemMatchesChart(item, c));
      if (!chart || chart.type !== 'kpi') return item;
      const wantMinH = 2;
      if ((item.minH ?? 3) === wantMinH && item.maxH == null) return item;
      dirty = true;
      const next = { ...item, minH: wantMinH };
      if (next.maxH != null) delete next.maxH;
      return next;
    });
    if (dirty) onLayoutChange(updated);
  }, [charts, externalLayout, layoutLocked, presentationMode, onLayoutChange]);

  const handleLayoutChange = (newLayout) => {
    // presentationMode usa un layout derivado (displayLayout) solo para pintar: nunca
    // persistir esos altos temporales de vuelta al layout real del dashboard.
    if (layoutLocked || presentationMode) return;
    if (onLayoutChange) {
      onLayoutChange(
        newLayout.map((item) => {
          const chart = charts.find((c) => layoutItemMatchesChart(item, c));
          return chart ? normalizeLayoutItem(item, chart) : item;
        })
      );
    }
  };

  const handleMakeIndependentHidden = async (chartId) => {
    if (!onMakeInstanceIndependent || !currentDashboardId || !chartId) return;
    requestLeave(async () => {
      setForkingChartId(chartId);
      try {
        await onMakeInstanceIndependent({ dashboardId: currentDashboardId, instanceId: chartId });
      } finally {
        setForkingChartId(null);
      }
    });
  };

  const handleRemoveHiddenChart = (chart, libEntry) => {
    if (!onRemoveChart || !chart?.id) return;
    const title = chart.advancedConfig?.title || chart.label || 'Sin título';
    const isReference = libEntry?.isReference === true;
    const msg = isReference
      ? `¿Eliminar «${title}» de este dashboard?\n\nEs una referencia: el gráfico original se mantiene en la colección.`
      : `¿Eliminar «${title}» de este dashboard?\n\nNo se borra de la colección de originales si otros dashboards lo usan.`;
    requestLeave(() => {
      if (!window.confirm(msg)) return;
      onRemoveChart(chart.id);
    });
  };


  return (
    <Box
      sx={{
        height: contentGrowsWithPage ? 'auto' : '100%',
        minHeight: contentGrowsWithPage ? 0 : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {charts.length === 0 ? (
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.default',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              No hay gráficos en el dashboard
            </Typography>
            {onOpenChartBuilder ? (
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => requestLeave(() => onOpenChartBuilder())}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  textTransform: 'none',
                }}
              >
                Agregar mi primer gráfico
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Usa el Editor de gráficos de la barra superior para crear visualizaciones.
              </Typography>
            )}
          </Box>
        </Paper>
      ) : (
        <Box 
          ref={containerRef}
          className="chart-grid-scroll"
          sx={{
            flex: contentGrowsWithPage ? '0 0 auto' : 1,
            overflowY: contentGrowsWithPage ? 'visible' : 'auto',
            overflowX: printCapture ? 'visible' : 'hidden',
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: contentGrowsWithPage ? 0 : undefined,
          }}
        >
          {!printCapture && hiddenCharts.length > 0 && (
            <Paper variant="outlined" sx={{ mb: 1, flexShrink: 0, bgcolor: 'grey.50' }}>
              <Button
                size="small"
                fullWidth
                onClick={() => setHiddenChartsOpen((v) => !v)}
                endIcon={hiddenChartsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  py: 0.75,
                  px: 1.5,
                  color: 'text.primary',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityOffOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="body2" fontWeight={600}>
                    Gráficos ocultos ({hiddenCharts.length})
                  </Typography>
                </Box>
              </Button>
              <Collapse in={hiddenChartsOpen}>
                <Box sx={{ px: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {hiddenCharts.map(({ chart, libEntry }) => {
                    const isReference = libEntry?.isReference === true;
                    const title = chart.advancedConfig?.title || chart.label || 'Sin título';
                    return (
                      <Box
                        key={chart.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          border: 1,
                          borderStyle: isReference ? 'dashed' : 'solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={500} noWrap title={title}>
                            {title}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {isReference && (
                              <Chip
                                size="small"
                                icon={<LinkOutlinedIcon sx={{ fontSize: 14 }} />}
                                label="Referencia"
                                variant="outlined"
                                sx={{ height: 22 }}
                              />
                            )}
                            <Chip size="small" label="No visible" variant="outlined" sx={{ height: 22 }} />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexShrink: 0, gap: 0.25 }}>
                          {isReference && onMakeInstanceIndependent && (
                            <Tooltip title="Desacoplar: convertir en original independiente">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={forkingChartId === chart.id}
                                  onClick={() => handleMakeIndependentHidden(chart.id)}
                                >
                                  {forkingChartId === chart.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <CallSplitOutlinedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {onShowChartOnGrid && (
                            <Tooltip title="Mostrar en el grid">
                              <IconButton size="small" onClick={() => onShowChartOnGrid(chart.id)}>
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onEditChart && (
                            <Tooltip title={isReference ? 'Ver referencia' : 'Editar'}>
                              <IconButton size="small" onClick={() => requestLeave(() => onEditChart(chart))}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onRemoveChart && (
                            <Tooltip title="Eliminar del dashboard">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveHiddenChart(chart, libEntry)}
                                sx={{
                                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Paper>
          )}
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {chartsDataLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 10,
                borderRadius: 1,
              }}
            >
              <CircularProgress size={48} />
            </Box>
          )}
          {/* Presentación: franja con los filtros globales ya promovidos, sin el control
              "Añadir filtro global" (acción de edición). */}
          {presentationMode && (() => {
            const presentationGlobalParamNames = globalFilterParamNames || [];
            const showGlobalFilters =
              availableParamNames.length > 0 &&
              Boolean(onGlobalFilterValuesChange) &&
              (embedMode ? (globalFilterParamNames || []).length > 0 : Boolean(onGlobalFilterParamNamesChange) && !readOnlyMode) &&
              presentationGlobalParamNames.length > 0;
            if (!showGlobalFilters) return null;
            return (
            <Box
              sx={{
                mb: 1.5,
                py: 1,
                px: '15px',
                bgcolor: '#fff',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                borderBottom: `1px solid ${vsaasTheme.border.light}`,
              }}
            >
              {showGlobalFilters && presentationGlobalParamNames.map((paramName) => {
                const value = globalFilterValues[paramName];
                const df = chartFilterEntries.find((e) => e.paramName === paramName)?.df;
                const label = labelForFilterParam(paramName, df, globalFilterParamAliases);
                if (df?.filterType === 'date') {
                  return (
                    <FilterDatePill
                      key={paramName}
                      label={label}
                      value={value ?? df.selectedValue ?? ''}
                      defaultValue={df.selectedValue}
                      onChange={(val) => {
                        onGlobalFilterValuesChange?.((prev) => ({ ...prev, [paramName]: val }));
                      }}
                    />
                  );
                }
                const options = resolveGlobalFilterOptions(paramName);
                const effectiveValue =
                  (value == null || String(value).trim() === '') && df?.allowExecuteWithoutSelection === true
                    ? ''
                    : value;
                const selectedOption = options.find((o) => String(o.value) === String(effectiveValue ?? ''));
                return (
                  <DynamicFilterSelect
                    key={paramName}
                    preferNativeSelect={preferNativeFilterSelect}
                    time={isTimeWindowFilter(df)}
                    label={label}
                    defaultValue={df?.selectedValue}
                    options={options}
                    value={selectedOption ?? null}
                    onChange={(newVal) => {
                      const val = newVal?.value;
                      onGlobalFilterValuesChange?.((prev) => ({ ...prev, [paramName]: val ?? '' }));
                    }}
                    disabled={!options.length}
                    placeholder="Seleccionar…"
                  />
                );
              })}
            </Box>
            );
          })()}
          {!presentationMode &&
            availableParamNames.length > 0 && onGlobalFilterValuesChange && (
              embedMode
                ? (globalFilterParamNames || []).length > 0
                : onGlobalFilterParamNamesChange && !readOnlyMode
            ) && (
            <Box
              sx={{
                mb: 1.5,
                py: 1,
                px: '15px',
                bgcolor: '#fff',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                borderBottom: `1px solid ${vsaasTheme.border.light}`,
              }}
            >
              {availableParamNames.length > 0 && onGlobalFilterValuesChange && (
                embedMode
                  ? (globalFilterParamNames || []).length > 0
                  : onGlobalFilterParamNamesChange && !readOnlyMode
              ) && (
                <>
                  {(globalFilterParamNames || []).map(paramName => {
                    const value = globalFilterValues[paramName];
                    const removeButton = !embedMode && !readOnlyMode && onGlobalFilterParamNamesChange && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          onGlobalFilterParamNamesChange(prev => prev.filter(p => p !== paramName));
                          onGlobalFilterValuesChange(prev => {
                            const next = { ...prev };
                            delete next[paramName];
                            return next;
                          });
                        }}
                        title="Quitar de filtros globales"
                        sx={{ p: 0.25 }}
                      >
                        <LinkOffIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    );
                    const df = chartFilterEntries.find((e) => e.paramName === paramName)?.df;
                    const label = labelForFilterParam(paramName, df, globalFilterParamAliases);
                    if (df?.filterType === 'date') {
                      return (
                        <Box key={paramName} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <FilterDatePill
                            label={label}
                            value={value ?? df.selectedValue ?? ''}
                            defaultValue={df.selectedValue}
                            onChange={(val) => {
                              onGlobalFilterValuesChange((prev) => ({ ...prev, [paramName]: val }));
                            }}
                          />
                          {removeButton}
                        </Box>
                      );
                    }
                    const options = resolveGlobalFilterOptions(paramName);
                    const effectiveValue =
                      (value == null || String(value).trim() === '') && df?.allowExecuteWithoutSelection === true
                        ? ''
                        : value;
                    const selectedOption = options.find(o => String(o.value) === String(effectiveValue ?? ''));
                    return (
                      <Box key={paramName} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <DynamicFilterSelect
                          preferNativeSelect={preferNativeFilterSelect}
                          time={isTimeWindowFilter(df)}
                          label={label}
                          defaultValue={df?.selectedValue}
                          options={options}
                          value={selectedOption ?? null}
                          onChange={(newVal) => {
                            const val = newVal?.value;
                            onGlobalFilterValuesChange((prev) => ({ ...prev, [paramName]: val ?? '' }));
                          }}
                          disabled={!options.length}
                          placeholder="Seleccionar…"
                        />
                        {removeButton}
                      </Box>
                    );
                  })}
                  {!embedMode && !readOnlyMode && onGlobalFilterParamNamesChange && availableParamNames.some(p => !(globalFilterParamNames || []).includes(p)) && (
                    <>
                      <Box
                        component="button"
                        type="button"
                        onClick={(e) => setAddGlobalAnchor(e.currentTarget)}
                        sx={(theme) => ({
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          height: 28,
                          px: 1.25,
                          borderRadius: 999,
                          border: '1px dashed',
                          borderColor: 'divider',
                          bgcolor: 'transparent',
                          color: 'text.secondary',
                          fontSize: 13,
                          fontFamily: theme.typography.fontFamily,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        })}
                      >
                        <AddIcon sx={{ fontSize: 15 }} />
                        Filtro global
                      </Box>
                      <Menu
                        anchorEl={addGlobalAnchor}
                        open={Boolean(addGlobalAnchor)}
                        onClose={() => setAddGlobalAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      >
                        {availableParamNames
                          .filter(p => !(globalFilterParamNames || []).includes(p))
                          .map(p => {
                            const df = chartFilterEntries.find((e) => e.paramName === p)?.df;
                            return (
                              <MenuItem
                                key={p}
                                onClick={() => {
                                  onGlobalFilterParamNamesChange(prev => [...prev, p]);
                                  setAddGlobalAnchor(null);
                                }}
                              >
                                {labelForFilterParam(p, df, globalFilterParamAliases) ||
                                  (isTimeWindowFilter(df) ? 'Ventana de tiempo' : humanizeFilterParam(p) || p)}
                              </MenuItem>
                            );
                          })}
                      </Menu>
                    </>
                  )}
                </>
              )}
            </Box>
          )}
          <GridLayout
            className="layout"
            layout={layoutForGrid}
            cols={12}
            rowHeight={gridRowHeight}
            width={containerWidth > 0 ? containerWidth : 320}
            onLayoutChange={handleLayoutChange}
            draggableHandle={(layoutLocked || presentationMode) ? undefined : '.drag-handle'}
            isDraggable={!layoutLocked && !presentationMode}
            isResizable={!layoutLocked && !presentationMode}
            compactType="vertical"
            preventCollision={false}
          >
            {charts.map((chart) => {
              const libEntry = (chartLibrary || []).find((e) => e.id === chart.id);
              const isReference = libEntry?.isReference === true;
              const isSharedInstance =
                chart.originalChartId &&
                sharedOriginalIds &&
                (sharedOriginalIds instanceof Set
                  ? sharedOriginalIds.has(chart.originalChartId)
                  : sharedOriginalIds[chart.originalChartId]);
              // Clave compuesta (queryId|params) para que al cambiar el filtro se muestren los datos correctos
              const dataKey = typeof getQueryDataKey === 'function' ? getQueryDataKey(chart.queryId, buildParamsForChart(chart), chart) : chart.queryId;
              const dataForKey = chart.queryId != null ? queryDataByQueryId[dataKey] : null;
              const activeFiltersForData = getChartDynamicFilters(chart).filter((f) => f?.enabled && f?.paramName && !isParamGlobal(f.paramName));
              const hasDynamicFilterNoOptions = activeFiltersForData.some((f) => {
                const optionKey = `${chart.id}::${String(f.paramName).trim()}`;
                return Array.isArray(dynamicFilterOptions[optionKey]) && dynamicFilterOptions[optionKey].length === 0;
              });
              // Si este gráfico tiene queryId, nunca usar queryData global: esa cache es de
              // otra consulta (p. ej. el editor SQL) y suele no traer las columnas del spec
              // (eje X vacío → "(sin dato)" en barras). Sin datos todavía: cargar, salvo
              // filtro dinámico sin opciones (vacío, no spinner infinito).
              let chartData = [];
              let chartDataLoading = false;
              if (chart.queryId != null && dataForKey?.data != null) {
                chartData = dataForKey.data;
              } else if (chart.queryId != null && !hasDynamicFilterNoOptions) {
                chartData = [];
                chartDataLoading = true;
              } else if (chart.queryId != null && hasDynamicFilterNoOptions) {
                chartData = [];
              } else {
                chartData = queryData?.data || [];
              }
              // Valor actual de cada filtro (fijo o con selección dinámica), para que el texto
              // narrativo pueda usar {{nombreDeColumna}} (slugificado) y no tenga que repetir
              // a mano el valor fijo en cada copia del gráfico por filtro. Se junta en un
              // arreglo por columna, no se pisa: dos filtros sobre la misma columna (ej. un
              // rango "desde"/"hasta") quedan los dos, unidos más abajo.
              const chartFilterValuesRaw = {};
              (Array.isArray(chart.visualizationSpec?.filters) ? chart.visualizationSpec.filters : []).forEach((f) => {
                if (!f?.column) return;
                // Un filtro dinámico local (dropdown propio del tile, ya resuelto en
                // activeFiltersForData) refleja lo que el usuario eligió ahora mismo; tiene
                // prioridad sobre el valor fijo/global guardado en el spec, que es solo el
                // que tenía el gráfico al publicarse.
                const liveDynamic = f.paramName
                  ? activeFiltersForData.find((df) => String(df.paramName || '').trim() === String(f.paramName).trim())
                  : null;
                const resolved =
                  liveDynamic && liveDynamic.selectedValue != null && liveDynamic.selectedValue !== ''
                    ? liveDynamic.selectedValue
                    : f.paramName && globalFilterValues[f.paramName] != null
                      ? globalFilterValues[f.paramName]
                      : (f.value ?? f.previewValue ?? f.selectedValue);
                if (resolved == null || resolved === '') return;
                if (!chartFilterValuesRaw[f.column]) chartFilterValuesRaw[f.column] = [];
                chartFilterValuesRaw[f.column].push(resolved);
              });
              const chartFilterValues = {};
              Object.entries(chartFilterValuesRaw).forEach(([column, values]) => {
                chartFilterValues[column] = values.length > 1 ? values.join(' - ') : values[0];
              });
              const chartCalcDefs = chart.advancedConfig?.tableCalculatedColumns
                || chart.visualizationSpec?.visualization?.tableCalculatedColumns
                || [];
              const calcAxis = calculatedAxisMapping(chart, dataForKey);
              const calcYColumn = calcAxis?.yAlias || chart.yColumn;
              if (chart.type !== 'table' && chart.type !== 'tablePlus' && chartCalcDefs.length) {
                chartData = applyCalculatedColumns(chartData, chartCalcDefs);
                if (chart.type === 'kpi' && isCalculatedColumnKey(calcYColumn)) {
                  const def = normalizeCalculatedColumns(chartCalcDefs).find((item) => item.key === calcYColumn);
                  if (def && def.op !== 'concat') chartData = sumCalculatedColumn(chartData, calcYColumn);
                }
              }
              // Snapshot de las filas SIN pivotear, antes de que el bloque de render mas abajo
              // reasigne "chartData" (let, misma closure) al resultado ya pivoteado. El boton
              // de exportar corre mucho despues (al hacer click), y para entonces "chartData"
              // ya apunta a las filas pivoteadas: pivotearlas de nuevo ahi no encuentra las
              // claves originales (bucketKey/rowKey) en filas que ya no las tienen, y el
              // export sale vacio.
              const rawChartDataForExport = chartData;
              // Sin datos todavia para el queryId de este grafico (primera carga en curso):
              // no dejar exportar hasta que llegue la respuesta real.
              const chartExportPending = chartDataLoading || (chart.queryId != null && dataForKey == null);
              const kpiReadonlyChipRowOnly = kpiTileFilterRowIsReadonlyChipsOnly(
                chart,
                isParamGlobal,
                Boolean(runQueryWithParams)
              );
              const kpiCompactReadonlyChip = chart.type === 'kpi' && kpiReadonlyChipRowOnly;
              const professionalPeriodMeta = presentationMode || printCapture;
              const periodCaption = professionalPeriodMeta
                ? getChartPeriodCaption(chart, {
                    globalFilterValues,
                    globalFilterParamNames,
                    globalFilterParamAliases,
                    getDynamicFilterValue: (_c, paramName) => {
                      const optionKey = `${chart.id}::${String(paramName || '').trim()}`;
                      return dynamicFilterValue[optionKey];
                    },
                  })
                : null;
              const kpiCompactTile = compactExportTiles && chart.type === 'kpi';
              const kpiEditCompactChrome =
                chart.type === 'kpi' && !presentationMode && !printCapture && !kpiCompactTile;
              const showPeriodCaptionInHeader = professionalPeriodMeta && periodCaption && !kpiCompactTile;
              return (
              <Box
                key={chart.id}
                ref={chart.id === highlightChartId ? chartHighlightRef : undefined}
                className={chart.id === highlightChartId ? 'chart-highlight-light' : undefined}
                sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    p:
                      chart.type === 'table' || chart.type === 'tablePlus'
                        ? 1
                        : kpiCompactTile
                          ? 1
                          : kpiEditCompactChrome
                            ? 1
                            : 2,
                    position: 'relative',
                    border: 1,
                    borderStyle: isSharedInstance ? 'dashed' : 'solid',
                    borderColor: isSharedInstance ? 'text.secondary' : vsaasTheme.border.light,
                  }}
                >
                  {!kpiCompactTile && (
                  <Box
                    className={(layoutLocked || presentationMode) ? undefined : 'drag-handle'}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 0.75, sm: 0 },
                      mb: kpiEditCompactChrome ? 0.25 : 1,
                      cursor: (layoutLocked || presentationMode) ? 'default' : 'move',
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                      <Typography
                        variant={kpiEditCompactChrome ? 'subtitle2' : 'subtitle1'}
                        fontWeight={600}
                        color="text.primary"
                        sx={{ wordBreak: 'break-word', lineHeight: kpiEditCompactChrome ? 1.2 : undefined }}
                      >
                        {chart.advancedConfig?.title || chart.label}
                      </Typography>
                      {showPeriodCaptionInHeader && (
                        <Box sx={tilePeriodCaptionRowSx} title={periodCaption.title || periodCaption.text}>
                          <EventOutlinedIcon sx={{ fontSize: '0.875rem', opacity: 0.85 }} aria-hidden />
                          <Typography variant="caption" component="span" sx={tilePeriodCaptionTextSx}>
                            {periodCaption.text}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignSelf: { xs: 'flex-end', sm: 'auto' } }} className="chart-actions" onMouseDown={(e) => e.stopPropagation()}>
                      {!layoutLocked && !presentationMode && chart.queryId && runQueryWithParams && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleRefreshChart(chart, e)}
                          disabled={refreshingChartIds.has(chart.id)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                          }}
                          title="Actualizar datos del gráfico"
                        >
                          {refreshingChartIds.has(chart.id) ? (
                            <CircularProgress size={16} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                      {!printCapture && !presentationMode && (() => {
                        const isWeather = chart.type === 'weather';
                        const isCalendar = chart.type === 'calendar';
                        const isButton = chart.type === 'button';
                        const weatherReady = Boolean(weatherDataByChartId[chart.id]);
                        const exportPending = isWeather ? !weatherReady : (isCalendar || isButton) ? false : chartExportPending;
                        return (
                          <IconButton
                            size="small"
                            disabled={exportPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (isWeather) {
                                downloadChartExcel(chart, weatherRowsForExport(weatherDataByChartId[chart.id]));
                              } else if (isCalendar) {
                                downloadChartExcel(chart, calendarRowsForExport(chart.calendarEvents));
                              } else if (isButton) {
                                downloadChartExcel(chart, buttonRowsForExport(chart));
                              } else {
                                // Usar rawChartDataForExport (capturado antes de que el render
                                // de mas abajo reasigne "chartData" al resultado ya pivoteado),
                                // para no depender del orden de evaluacion del JSX ni pivotear
                                // dos veces las mismas filas (eso las deja vacias: la segunda
                                // vuelta busca columnas que las filas pivoteadas ya no tienen).
                                const exportPivotConfig = dateBucketPivotConfigForChart(chart, rawChartDataForExport);
                                downloadChartExcel(
                                  chart,
                                  tableRowsForDisplay(chart, rawChartDataForExport),
                                  exportPivotConfig?.rowHeaderLabel
                                    ? { [exportPivotConfig.rowKey]: exportPivotConfig.rowHeaderLabel }
                                    : undefined,
                                  exportPivotConfig
                                    ? dateBucketPivotColumnOrder(exportPivotConfig.rowKey, exportPivotConfig.bucketType)
                                    : undefined,
                                  // La tabla pivoteada siempre muestra la columna Total en
                                  // pantalla (showTotalColumn sin condicion en ese branch).
                                  Boolean(exportPivotConfig)
                                );
                              }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{
                              cursor: exportPending ? 'default' : 'pointer',
                              '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                            }}
                            title={
                              exportPending
                                ? (isWeather ? 'Esperando a que cargue el clima' : 'Esperando a que termine de cargar el gráfico')
                                : 'Descargar Excel'
                            }
                          >
                            <FileDownloadIcon fontSize="small" />
                          </IconButton>
                        );
                      })()}
                      {!layoutLocked && !presentationMode && chart.type === 'calendar' && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setCalendarSettingsFor({ chartId: chart.id, anchorEl: e.currentTarget });
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                          }}
                          title="Editar feriados y eventos"
                        >
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      )}
                      {!layoutLocked && !presentationMode && onEditChart && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            requestLeave(() => onEditChart(chart));
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            },
                          }}
                          title={
                            isReference
                              ? 'Referencia — abre vista informativa (no editable directamente)'
                              : 'Editar gráfico'
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {!layoutLocked && !presentationMode && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleRemoveFromGrid(chart.id, e);
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                          },
                        }}
                        title={onHideChart ? 'Ocultar del grid (conservar en el dashboard)' : 'Eliminar gráfico'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      )}
                    </Box>
                  </Box>
                  )}
                  {chart.queryId && !professionalPeriodMeta && getChartDynamicFilters(chart).some((f) => f?.enabled && f?.paramName) && (
                    <Box sx={kpiCompactReadonlyChip ? kpiFilterChipRowSx : filterPillRowSx}>
                      {sortChartDynamicFilters(getChartDynamicFilters(chart))
                        .filter((f) => f?.enabled && f?.paramName)
                        .map((df) => {
                          const paramName = String(df.paramName).trim();
                          const optionKey = `${chart.id}::${paramName}`;
                          const pillLabel = labelForFilterParam(paramName, df, globalFilterParamAliases);
                          const time = isTimeWindowFilter(df);
                          if (isParamGlobal(paramName)) {
                            const globalChipValue = (() => {
                              const val = globalFilterValues[paramName];
                              if (val == null || String(val).trim() === '') {
                                return df.allowExecuteWithoutSelection === true ? 'Cualquiera' : '—';
                              }
                              const opts = resolveGlobalFilterOptions(paramName);
                              const opt = opts.find((o) => String(o.value) === String(val));
                              if (opt && opt.label != null && String(opt.label).trim() !== '') {
                                return String(opt.label).trim();
                              }
                              return formatDynamicFilterChipValue(val);
                            })();
                            return (
                              <FilterValuePill
                                key={`global-${optionKey}`}
                                label={pillLabel}
                                value={globalChipValue}
                                time={time}
                                title={`${pillLabel || paramName}: ${globalChipValue} (global)`}
                              />
                            );
                          }
                          // Filtro local del tile. Si el param está promovido a global, la barra
                          // de arriba es la fuente de verdad (caso anterior).
                          if (runQueryWithParams) {
                            if (df.filterType === 'date') {
                              return (
                                <FilterDatePill
                                  key={`date-${optionKey}`}
                                  label={pillLabel}
                                  value={dynamicFilterValue[optionKey] ?? df.selectedValue ?? ''}
                                  defaultValue={df.selectedValue}
                                  onChange={(val) => {
                                    setDynamicFilterValue((prev) => ({ ...prev, [optionKey]: val }));
                                    if (paramName) {
                                      runQueryWithParams?.(
                                        chart.queryId,
                                        buildParamsForChart(chart, { [paramName]: val }),
                                        chart
                                      );
                                      onDynamicFilterChange?.(chart.id, paramName, val);
                                    }
                                  }}
                                />
                              );
                            }
                            return (
                              <DynamicFilterSelect
                                key={`local-${optionKey}`}
                                preferNativeSelect={preferNativeFilterSelect}
                                time={time}
                                label={pillLabel}
                                defaultValue={df.selectedValue}
                                options={resolveFilterOptions(optionKey, chart, paramName)}
                                value={(() => {
                                  const opts = resolveFilterOptions(optionKey, chart, paramName);
                                  const raw =
                                    dynamicFilterValue[optionKey] ??
                                    (df.allowExecuteWithoutSelection === true ? '' : df.selectedValue);
                                  return (
                                    opts.find((o) => String(o.value) === String(raw ?? '')) ?? null
                                  );
                                })()}
                                onChange={(newVal) => {
                                  const val = newVal?.value ?? '';
                                  setDynamicFilterValue((prev) => ({ ...prev, [optionKey]: val }));
                                  if (paramName) {
                                    runQueryWithParams?.(
                                      chart.queryId,
                                      buildParamsForChart(chart, { [paramName]: val }),
                                      chart
                                    );
                                    onDynamicFilterChange?.(chart.id, paramName, val);
                                  }
                                }}
                                disabled={!resolveFilterOptions(optionKey, chart, paramName).length}
                                placeholder="Seleccionar…"
                              />
                            );
                          }
                          const readonlyChipValue = formatDynamicFilterChipValue(df.selectedValue);
                          const readonlyChipLabel = pillLabel || paramName;
                          return (
                            <FilterValuePill
                              key={`readonly-${optionKey}`}
                              label={readonlyChipLabel}
                              value={readonlyChipValue}
                              time={time}
                              title={`${readonlyChipLabel}: ${readonlyChipValue}`}
                            />
                          );
                        })}
                    </Box>
                  )}
                  <Box
                    className="chart-widget-body"
                    sx={{
                      flex: 1,
                      minHeight:
                        chart.type === 'heatmapGrid' || chart.type === 'imageDensity' || chart.type === 'imageCarousel' || chart.type === 'geoHeatmap' ? 220 : 0,
                      width: '100%',
                      minWidth: 0,
                      overflow: kpiCompactTile ? 'visible' : 'hidden',
                      pt: 0.15,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent:
                        chart.type === 'kpi' ? 'center' : 'stretch',
                    }}
                  >
                    {(() => {
                      if (chart.type === 'weather') {
                        return (
                          <WeatherWidget
                            lat={chart.weatherConfig?.lat}
                            lon={chart.weatherConfig?.lon}
                            getAuthHeaders={getAuthHeaders}
                            onDataLoaded={(weatherData) => onWeatherDataLoaded?.(chart.id, weatherData)}
                          />
                        );
                      }
                      if (chart.type === 'calendar') {
                        return <CalendarWidget events={chart.calendarEvents} />;
                      }
                      if (chart.type === 'button') {
                        return <ButtonWidget label={chart.label} url={chart.buttonUrl} />;
                      }
                      const heatmapPositions = resolveCommittedHeatmapPositions(chart, chartData);
                      if (heatmapPositions) {
                        chartData = applyCommittedHeatmapPositionColumns(chartData, chart);
                      }
                      // Si la columna de fecha se marcó "Pivote en tabla", mostrar la otra
                      // columna Fila en filas y el bucket (hora, día de semana, día del mes
                      // o mes) en columnas, en vez de una fila por valor del bucket. La
                      // columna "Total" de DataTable ya suma todo lo numerico de la fila,
                      // asi que sirve directo como columna de totales. Esto solo afecta lo
                      // que se renderiza aqui; el export del tile (mas arriba en este mismo
                      // componente) llama a tableRowsForDisplay por su cuenta, no depende de
                      // esta mutacion de chartData.
                      const dateBucketPivotConfig = dateBucketPivotConfigForChart(chart, chartData);
                      if (dateBucketPivotConfig) {
                        chartData = tableRowsForDisplay(chart, chartData);
                      }
                      const dataLoading = chartDataLoading;
                      // Normalizar advancedConfig con defaults para que coincida con la preview (evita diferencias si viene null o incompleto)
                      const durationPresentation = resolveEpisodeDurationPresentation(
                        chart.visualizationSpec?.episodeAnalysis,
                        chart.visualizationSpec?.tableColumns,
                        chart.yColumn
                      );
                      const kpiValueFontSizeFromAdv = chart.advancedConfig?.kpiValueFontSize;
                      const kpiValueFontSizeFromSpec =
                        chart.visualizationSpec?.visualization?.kpiValueFontSize;
                      const resolvedKpiValueFontSize =
                        kpiValueFontSizeFromAdv === 'small' || kpiValueFontSizeFromAdv === 'medium'
                          ? kpiValueFontSizeFromAdv
                          : kpiValueFontSizeFromSpec === 'small' || kpiValueFontSizeFromSpec === 'medium'
                            ? kpiValueFontSizeFromSpec
                            : undefined;
                      const advancedConfig = {
                        ...DEFAULT_ADVANCED_CONFIG,
                        ...(chart.advancedConfig && typeof chart.advancedConfig === 'object' ? chart.advancedConfig : {}),
                        ...(durationPresentation.yDuration || {}),
                        ...(chart.type === 'kpi' && dataForKey?.kpiComparison
                          ? { kpiComparison: dataForKey.kpiComparison }
                          : {}),
                        ...(chart.type === 'kpi' && resolvedKpiValueFontSize
                          ? { kpiValueFontSize: resolvedKpiValueFontSize }
                          : {}),
                      };

                      // Con comparacion de periodo activa, la consulta devuelve un dataset
                      // sintetico (etiqueta/serie/valor) con nombres de columna distintos a
                      // los que el grafico tiene guardados; el mapping que llega junto a los
                      // datos es la unica fuente confiable de como se llaman esas columnas.
                      // Se detecta por la forma real de la respuesta (los nombres de columna
                      // que arma ese dataset sintetico son siempre los mismos), no por el flag
                      // del spec guardado: si el usuario dejo la comparacion activada pero para
                      // este request en particular no se llego a armar (ej. sin filtro de
                      // fecha), la respuesta es la normal y no hay que pisar groupByColumn.
                      const comparisonMapping =
                        (chart.type === 'bar' || chart.type === 'line') &&
                        dataForKey?.mapping?.xAlias === 'periodo_label' &&
                        dataForKey?.mapping?.labelAlias === 'serie'
                          ? dataForKey.mapping
                          : null;
                      // Diferencia/porcentaje de variacion: puede venir del dataset sintetico de
                      // arriba (comparisonMapping) o, sin reemplazar columnas, de comparar dos
                      // valores de "Series/color" propios del grafico (ej. columna "año"). En
                      // ambos casos el servidor los expone con el mismo par de alias.
                      const metricMapping =
                        dataForKey?.mapping?.deltaAlias || dataForKey?.mapping?.percentAlias
                          ? dataForKey.mapping
                          : null;

                      if (dataLoading) {
                        const isKpiTile = chart.type === 'kpi';
                        return (
                          <Box
                            sx={{
                              flex: isKpiTile ? 1 : undefined,
                              width: '100%',
                              minHeight: isKpiTile ? 0 : 200,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: isKpiTile ? 0.5 : 1,
                              py: isKpiTile ? 0.5 : 0,
                              boxSizing: 'border-box',
                            }}
                          >
                            <CircularProgress size={isKpiTile ? 22 : 28} />
                            <Typography
                              variant={isKpiTile ? 'caption' : 'body2'}
                              color="text.secondary"
                              sx={{ textAlign: 'center', px: isKpiTile ? 0.5 : 0, lineHeight: 1.25 }}
                            >
                              {isKpiTile
                                ? 'Cargando…'
                                : 'Cargando datos con el filtro seleccionado…'}
                            </Typography>
                          </Box>
                        );
                      }

                      return chart.type === 'tablePlus' ? (
                        <TablePlusView
                          data={chartData}
                          specColumns={
                            (chart.visualizationSpec?.visualization?.chartFeed || 'base') === 'base'
                              ? chart.visualizationSpec?.tableColumns
                              : undefined
                          }
                          visibleColumns={advancedConfig.tableVisibleColumns ?? undefined}
                          groupColumns={advancedConfig.tablePlusGroupColumns}
                          applyTimezone={advancedConfig.tablePlusApplyTimezone !== false}
                          timeZone={dashboardTimezone}
                          printCapture={printCapture}
                          showTotalColumn={advancedConfig.tableShowTotalColumn}
                          showTotalRow={advancedConfig.tableShowTotalRow}
                          columnDecimals={advancedConfig.tableColumnDecimals ?? undefined}
                          columnDateFormats={advancedConfig.tableColumnDateFormats ?? undefined}
                          columnDateBuckets={advancedConfig.tableColumnDateBuckets ?? undefined}
                          columnColorThresholds={advancedConfig.tableColumnColorThresholds ?? undefined}
                          columnLabels={resolveTableColumnLabels(chart) ?? {}}
                          columnDurationFormats={
                            advancedConfig.tableColumnDurationFormats ??
                            durationPresentation.tableDurationFormats
                          }
                          columnDurationTooltipFormats={durationPresentation.tableDurationTooltipFormats}
                          columnFilters={advancedConfig.tableColumnFilters ?? {}}
                          calculatedColumns={advancedConfig.tableCalculatedColumns || []}
                          onColumnFilterChange={(col, values) => {
                            const next = { ...(advancedConfig.tableColumnFilters || {}), [col]: values };
                            updateCharts(charts.map((c) => (c.id === chart.id ? { ...c, advancedConfig: { ...c.advancedConfig, tableColumnFilters: next } } : c)));
                          }}
                          onClearAllFilters={() => {
                            updateCharts(charts.map((c) => (c.id === chart.id ? { ...c, advancedConfig: { ...c.advancedConfig, tableColumnFilters: {} } } : c)));
                          }}
                        />
                      ) : chart.type === 'table' && dateBucketPivotConfig ? (
                        // La config por columna (visibleColumns, filtros, formatos...) esta
                        // pensada para las columnas originales, no aplica una vez pivoteado
                        // a grupo x bucket, asi que no se reusa aqui.
                        <DataTable
                          data={chartData}
                          printCapture={printCapture}
                          showTotalColumn
                          visibleColumns={dateBucketPivotColumnOrder(
                            dateBucketPivotConfig.rowKey,
                            dateBucketPivotConfig.bucketType
                          )}
                          columnLabels={
                            dateBucketPivotConfig.rowHeaderLabel
                              ? { [dateBucketPivotConfig.rowKey]: dateBucketPivotConfig.rowHeaderLabel }
                              : undefined
                          }
                          showSortHint={false}
                        />
                      ) : chart.type === 'table' ? (
                        <DataTable
                          data={chartData}
                          printCapture={printCapture}
                          showTotalColumn={advancedConfig.tableShowTotalColumn}
                          showTotalRow={advancedConfig.tableShowTotalRow}
                          visibleColumns={
                            heatmapPositions && advancedConfig.tableVisibleColumns
                              ? appendHeatmapPositionVisibleColumns(advancedConfig.tableVisibleColumns)
                              : advancedConfig.tableVisibleColumns ?? undefined
                          }
                          columnDecimals={advancedConfig.tableColumnDecimals ?? undefined}
                          columnDateFormats={advancedConfig.tableColumnDateFormats ?? undefined}
                          columnDateBuckets={advancedConfig.tableColumnDateBuckets ?? undefined}
                          columnColorThresholds={advancedConfig.tableColumnColorThresholds ?? undefined}
                          columnLabels={resolveTableColumnLabels(chart) ?? {}}
                          columnImageColumns={advancedConfig.tableColumnImageColumns ?? undefined}
                          columnDurationFormats={
                            advancedConfig.tableColumnDurationFormats ??
                            durationPresentation.tableDurationFormats
                          }
                          columnDurationTooltipFormats={durationPresentation.tableDurationTooltipFormats}
                          columnFilters={advancedConfig.tableColumnFilters ?? {}}
                          calculatedColumns={advancedConfig.tableCalculatedColumns || []}
                          onColumnFilterChange={(col, values) => {
                            const next = { ...(advancedConfig.tableColumnFilters || {}), [col]: values };
                            updateCharts(charts.map((c) => (c.id === chart.id ? { ...c, advancedConfig: { ...c.advancedConfig, tableColumnFilters: next } } : c)));
                          }}
                          onClearAllFilters={() => {
                            updateCharts(charts.map((c) => (c.id === chart.id ? { ...c, advancedConfig: { ...c.advancedConfig, tableColumnFilters: {} } } : c)));
                          }}
                        />
                      ) : (
                        <ChartWidget
                          type={chart.type}
                          data={chartData}
                          xColumn={
                            comparisonMapping?.xAlias ||
                            calcAxis?.xAlias ||
                            dataForKey?.mapping?.xAlias ||
                            chart.xColumn
                          }
                          yColumn={
                            comparisonMapping?.yAlias ||
                            calcAxis?.yAlias ||
                            dataForKey?.mapping?.yAlias ||
                            chart.yColumn
                          }
                          groupByColumn={comparisonMapping || calcAxis ? undefined : chart.groupByColumn}
                          filterValues={chartFilterValues}
                          labelColumn={
                            comparisonMapping?.labelAlias ||
                            calcAxis?.labelAlias ||
                            dataForKey?.mapping?.labelAlias ||
                            chart.labelColumn
                          }
                          deltaColumn={metricMapping?.deltaAlias}
                          percentColumn={metricMapping?.percentAlias}
                          occupancyColumn={metricMapping?.occupancyAlias}
                          cumulativeColumn={metricMapping?.cumulativeAlias}
                          hourCycleStart={calcAxis?.hourCycleStart ?? chart.hourCycleStart}
                          xDateBucket={calcAxis?.xDateBucket || resolveXDateBucket(chart)}
                          xDateDisplayFormat={calcAxis?.xDateDisplayFormat || resolveXDateDisplayFormat(chart)}
                          labelDateBucket={calcAxis?.labelDateBucket || resolveLabelDateBucket(chart)}
                          labelDateDisplayFormat={calcAxis?.labelDateDisplayFormat || resolveLabelDateDisplayFormat(chart)}
                          stacked={chart.stacked}
                          horizontal={chart.horizontal}
                          advancedConfig={advancedConfig}
                          kpiValueFontSize={
                            chart.type === 'kpi' ? resolvedKpiValueFontSize : undefined
                          }
                          columnDurationFormats={
                            advancedConfig.tableColumnDurationFormats ??
                            durationPresentation.tableDurationFormats
                          }
                          heatmapGridConfig={chart.heatmapGridConfig}
                          imageDensityConfig={imageDensityConfigWithDates(chart, chartData)}
                          imageCarouselConfig={resolveImageCarouselConfig(chart, chartData)}
                          geoHeatmapConfig={resolveGeoHeatmapConfig(chart, chartData)}
                          intervalConfig={chart.intervalConfig || advancedConfig.intervalConfig}
                          mapConfig={chart.mapConfig}
                          label={chart.label}
                          getAuthHeaders={getAuthHeaders}
                          unsavedSessionId={chart.id}
                          chartZoomEnabled={!printCapture}
                          presentationMode={kpiCompactTile}
                          periodCaption={kpiCompactTile ? periodCaption : null}
                          onMapConfigChange={(patch) => {
                            if (!patch) return;
                            updateCharts(charts.map((c) => (
                              c.id === chart.id
                                ? { ...c, mapConfig: { ...(c.mapConfig || {}), ...patch } }
                                : c
                            )));
                          }}
                        />
                      );
                    })()}
                  </Box>
                </Paper>
              </Box>
            );
            })}
          </GridLayout>
          </Box>
        </Box>
      )}

      <Popover
        open={Boolean(calendarSettingsFor)}
        anchorEl={calendarSettingsFor?.anchorEl}
        onClose={() => setCalendarSettingsFor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ p: 2 }}>
          <CalendarEventsEditor
            events={charts.find((c) => c.id === calendarSettingsFor?.chartId)?.calendarEvents}
            onChange={(nextEvents) => {
              if (!calendarSettingsFor) return;
              const { chartId } = calendarSettingsFor;
              updateCharts(charts.map((c) => (c.id === chartId ? { ...c, calendarEvents: nextEvents } : c)));
            }}
            getAuthHeaders={getAuthHeaders}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default ChartGrid;
